import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Grid3X3, RotateCcw } from 'lucide-react';

const PAIRS = [
  { symbol: 'H', name: 'Hydrogen' }, { symbol: 'He', name: 'Helium' },
  { symbol: 'Li', name: 'Lithium' }, { symbol: 'C', name: 'Carbon' },
  { symbol: 'N', name: 'Nitrogen' }, { symbol: 'O', name: 'Oxygen' },
  { symbol: 'Na', name: 'Sodium' }, { symbol: 'Fe', name: 'Iron' },
];

const ElementMatch = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState(new Set());
  const [moves, setMoves] = useState(0);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const selected = PAIRS.slice(0, 6);
    const allCards = [];
    selected.forEach((p, i) => {
      allCards.push({ id: i*2, pairId: i, text: p.symbol, type: 'symbol' });
      allCards.push({ id: i*2+1, pairId: i, text: p.name, type: 'name' });
    });
    setCards(allCards.sort(() => Math.random() - 0.5));
  }, []);

  const handleFlip = (idx) => {
    if (flipped.length >= 2 || flipped.includes(idx) || matched.has(cards[idx].pairId)) return;
    const newFlipped = [...flipped, idx];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = newFlipped;
      if (cards[a].pairId === cards[b].pairId) {
        const newMatched = new Set(matched);
        newMatched.add(cards[a].pairId);
        setMatched(newMatched);
        setScore(s => s + 15);
        setFlipped([]);
        if (newMatched.size === 6) setTimeout(() => setCompleted(true), 500);
      } else {
        setTimeout(() => setFlipped([]), 800);
      }
    }
  };

  const restart = () => {
    const selected = PAIRS.slice(0, 6);
    const allCards = [];
    selected.forEach((p, i) => {
      allCards.push({ id: i*2, pairId: i, text: p.symbol, type: 'symbol' });
      allCards.push({ id: i*2+1, pairId: i, text: p.name, type: 'name' });
    });
    setCards(allCards.sort(() => Math.random() - 0.5));
    setFlipped([]); setMatched(new Set()); setMoves(0); setScore(0); setCompleted(false);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4">🧪</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">All Matched!</h2>
          <p className="text-4xl font-black text-amber-500 mb-2">{score} points</p>
          <p className="text-lg text-[#64748B] mb-8">Completed in {moves} moves</p>
          <div className="space-y-3">
            <button onClick={restart} className="w-full py-4 bg-amber-500 text-white rounded-2xl font-bold border-b-4 border-amber-700 active:border-b-0 active:translate-y-1 transition-all">Play Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50" data-testid="element-match-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3"><span className="bg-amber-500 text-white px-4 py-2 rounded-2xl font-bold">Moves: {moves}</span><span className="bg-[#4D96FF] text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span></div>
        </div>
        <h2 className="text-3xl font-black text-[#1E293B] text-center mb-6">Match Elements to Symbols!</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {cards.map((card, idx) => {
            const isFlipped = flipped.includes(idx);
            const isMatched = matched.has(card.pairId);
            return (
              <button key={card.id} onClick={() => handleFlip(idx)} data-testid={`card-${idx}`}
                className={`aspect-square rounded-2xl font-bold text-lg border-2 transition-all duration-300 ${
                  isMatched ? 'bg-green-100 border-green-400 text-green-700 scale-95' :
                  isFlipped ? 'bg-amber-100 border-amber-400 text-[#1E293B] rotate-0' :
                  'bg-gradient-to-br from-amber-400 to-amber-600 border-amber-600 text-white hover:scale-105 cursor-pointer'
                }`}>
                {isFlipped || isMatched ? (
                  <span className={card.type === 'symbol' ? 'text-2xl font-black' : 'text-sm'}>{card.text}</span>
                ) : (
                  <span className="text-3xl">?</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ElementMatch;
